import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiCard from '@mui/material/Card';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeIcon from '@mui/icons-material/Home';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ApiClient from '../utils/apiClient';

import { styled } from '@mui/material/styles';

import { OctopusIcon } from './CustomIcons';
import LoadingProgress from './LoadingProgress';
import ConnectingProgress from './ConnectingProgress';
import Grid from '@mui/material/Grid2';
import Divider from '@mui/material/Divider';

axios.defaults.headers.post['Content-Type'] = 'application/json';

// Regions array copied from db_context.py
const regions = [
  {"region_id": 1, "region_name": "North Scotland", "gsp_region_id": "P"},
  {"region_id": 2, "region_name": "South Scotland", "gsp_region_id": "N"},
  {"region_id": 3, "region_name": "North West England", "gsp_region_id": "G"},
  {"region_id": 4, "region_name": "North East England", "gsp_region_id": "F"},
  {"region_id": 5, "region_name": "Yorkshire", "gsp_region_id": "M"},
  {"region_id": 6, "region_name": "North Wales and Merseyside", "gsp_region_id": "D"},
  {"region_id": 7, "region_name": "South Wales", "gsp_region_id": "K"},
  {"region_id": 8, "region_name": "West Midlands", "gsp_region_id": "E"},
  {"region_id": 9, "region_name": "East Midlands", "gsp_region_id": "B"},
  {"region_id": 10, "region_name": "East England", "gsp_region_id": "A"},
  {"region_id": 11, "region_name": "South West England", "gsp_region_id": "L"},
  {"region_id": 12, "region_name": "South England", "gsp_region_id": "H"},
  {"region_id": 13, "region_name": "London", "gsp_region_id": "C"},
];

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  backgroundColor: `${theme.palette.background.default} !important`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2), // Reduce padding on xs/sm screens to match main card
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

interface ConnectOctopusCardProps {
  registrationComplete: boolean;
  isConnected: boolean;
  onConnectionChange: (connected: boolean) => void;
}

// Helper function to get grid supply point from Octopus API
const getGridSupplyPoint = async (postcode: string): Promise<string> => {
  try {
    const response = await axios.get('https://api.octopus.energy/v1/industry/grid-supply-points/', {
      params: { postcode },
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    const results = response.data.results;
    if (results.length === 0) {
      return 'X'; // Not found
    }
    
    return results[0].group_id.slice(-1); // Get last character of group_id
  } catch (error) {
    console.error('Error getting grid supply point:', error);
    return 'X'; // Not found
  }
};

// Helper function to map GSP region ID to region name
const getRegionName = (gspRegionId: string): string => {
  const region = regions.find(r => r.gsp_region_id === gspRegionId);
  return region ? region.region_name : 'Unknown Region';
};

export default function ConnectOctopusCard({ registrationComplete, isConnected, onConnectionChange }: ConnectOctopusCardProps) {
  const theme = useTheme();
  const [apiKeyError, setApiKeyError] = React.useState(false);
  const [apiKeyErrorMessage, setApiKeyErrorMessage] = React.useState('');
  const [accountNumberError, setAccountNumberError] = React.useState(false);
  const [accountNumberErrorMessage, setAccountNumberErrorMessage] = React.useState('');
  const [returnErrorMessage, setReturnErrorMessage] = React.useState('');

  const [showProgress, setShowProgress] = React.useState(false);
  const [showConnecting, setShowConnecting] = React.useState(false);
  const [loadedToDate, setLoadedToDate] = React.useState('');
  const [agreementPercentComplete, setAgreementPercentComplete] = React.useState(0);
  const [consumptionPercentComplete, setConsumptionPercentComplete] = React.useState(0);
  const [tariffName, setTariffName] = React.useState('');
  const [agreementPeriod, setAgreementPeriod] = React.useState('');
  const [propertyData, setPropertyData] = React.useState<any>(null);
  const [showConsumptionProgress, setShowConsumptionProgress] = React.useState(false);
  const [connectionProgressComplete, setConnectionProgressComplete] = React.useState(false);
  const [expandedProperties, setExpandedProperties] = React.useState<Set<number>>(new Set());
  const [apiKeyValue, setApiKeyValue] = React.useState('');
  const [accountNumberValue, setAccountNumberValue] = React.useState('');
  const navigate = useNavigate();

  // Effect that triggers when registrationComplete changes - now uses SSE
  React.useEffect(() => {
    if (registrationComplete) {
      // Start by showing the negotiating status
      setShowConnecting(true);
      setConnectionProgressComplete(false);

      // Use fetch to make a POST request and handle the SSE response
      const connectToOctopus = async () => {
        try {
          // Make POST request with headers
          const response = await fetch(`${ApiClient.getCurrentBaseUrl()}/api/connect/octopus`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem("token")}`
            },
            body: JSON.stringify({
              apiKey: apiKeyValue,
              accountNumber: accountNumberValue
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          setShowConnecting(false);
          setShowProgress(true);

          // Handle SSE stream
          const reader = response.body?.getReader();
          const decoder = new TextDecoder();

          if (reader) {
            while (true) {
              const { value, done } = await reader.read();
              if (done) break;

              const chunk = decoder.decode(value);
              const lines = chunk.split('\n');

              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix
                    console.log(data);
                    if (data.connect_complete === true) {
                      setConnectionProgressComplete(true);
                    } else {
                      setAgreementPercentComplete(data.percent_complete || 0);
                      setTariffName(data.tariff_name || 'Loading...');
                      setAgreementPeriod(data.agreement_period || 'Processing...');
                    }
                  } catch (parseError) {
                    console.error('Error parsing SSE data:', parseError);
                  }
                }
              }
            }
          }

        } catch (error) {
          console.error('Connection error:', error);
          setShowConnecting(false);
          setReturnErrorMessage('Failed to connect to Octopus Energy. Please check your credentials.');
        }
      };

      connectToOctopus();
    }
  }, [registrationComplete, apiKeyValue, accountNumberValue]);

  // Effect for consumption loading - triggers only when connection is complete
  React.useEffect(() => {
    if (connectionProgressComplete) {
      setShowProgress(false);
      setShowConsumptionProgress(true);
      setConsumptionPercentComplete(0); // Reset consumption progress
      setLoadedToDate(''); // Reset loaded to date
      
      const userId: any = localStorage.getItem("user_id");
      const eventSource = new EventSource(`${ApiClient.getCurrentBaseUrl()}/api/connect/load/octopus?id=${userId}`);
      let timeout: NodeJS.Timeout;

      // Function to reset a timeout to close the event source connection after a period of inactivity
      const resetTimeout = () => {
        clearTimeout(timeout); // Clears the existing timeout
        // Sets a new timeout to close the event source connection after 120000ms (2 minutes) of inactivity
        timeout = setTimeout(() => {
          console.warn('SSE connection timeout - closing connection');
          eventSource.close();
        }, 120000); 
      };

      // Event handler for receiving messages
      eventSource.onmessage = function(event) {
        resetTimeout(); // Resets the timeout on each message received to keep the connection open
        // Parses the incoming message and updates the component's state with the new data
        const receivedData = JSON.parse(event.data);
        console.log(receivedData);
        setLoadedToDate(receivedData.loaded_to_date);
        setConsumptionPercentComplete(receivedData.percent_complete);
        if (receivedData.load_complete) {
          clearTimeout(timeout);
          eventSource.close()
          navigate('/app', { replace: true });
          setShowConsumptionProgress(false)
        }
      };

      // Error handler for SSE connection
      eventSource.onerror = function(event) {
        console.error('SSE connection error:', event);
        clearTimeout(timeout);
        eventSource.close();
      };

      resetTimeout(); // Initialize the timeout when the component mounts

      // Cleanup function to clear the timeout and close the event source connection when the component unmounts
      return () => {
        clearTimeout(timeout);
        eventSource.close();
      };
    }
  }, [connectionProgressComplete, navigate]);

  const handleConnect = async () => {
    if (validateInputs()) {
      try {
        setShowConnecting(true)
        setReturnErrorMessage('')
        
        // Direct call to Octopus Energy API
        const response = await axios.get(`https://api.octopus.energy/v1/accounts/${accountNumberValue}/`, {
          auth: {
            username: apiKeyValue,
            password: ''
          },
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.status === 200) {
          setShowConnecting(false)
          
          // Process the account data similar to backend logic
          const accountDetails = response.data;
          
          if (accountDetails.properties && accountDetails.properties.length > 0) {
            // Build the account data structure similar to backend
            const propertiesWithRegions = await Promise.all(
              accountDetails.properties.map(async (property: any) => {
                // Get grid supply point and region name for this property
                const gspRegionId = await getGridSupplyPoint(property.postcode);
                const regionName = getRegionName(gspRegionId);
                
                return {
                  address_line_1: property.address_line_1,
                  address_line_2: property.address_line_2,
                  address_line_3: property.address_line_3,
                  town: property.town,
                  county: property.county,
                  postcode: property.postcode,
                  region_name: regionName,
                  electricity_meters: property.electricity_meter_points?.map((meterPoint: any) => ({
                    mpan: meterPoint.mpan,
                    serial_number: meterPoint.meters?.[0]?.serial_number || 'N/A'
                  })) || [],
                  gas_meters: property.gas_meter_points?.map((meterPoint: any) => ({
                    mprn: meterPoint.mprn,
                    serial_number: meterPoint.meters?.[0]?.serial_number || 'N/A'
                  })) || []
                };
              })
            );
            
            const accountData = {
              account_number: accountNumberValue,
              octopus_api_key: apiKeyValue,
              properties: propertiesWithRegions
            };
            
            setPropertyData(accountData)
            onConnectionChange(true)
          } else {
            setReturnErrorMessage('No properties found for this account.')
          }
        }
      } catch (error: any) {
        setShowConnecting(false)
        if (error.response) {
          // Handle HTTP errors similar to backend
          if (error.response.status === 401 || error.response.status === 403) {
            setReturnErrorMessage('Invalid API key or account number. Please check the values above and try again.')
          } else if (error.response.status === 404) {
            setReturnErrorMessage('Account not found. Please check your account number.')
          } else {
            setReturnErrorMessage('An error occurred while checking your account details.')
          }
          console.log('Error response:', error.response.data);
          console.log('Error status:', error.response.status);
        } else if (error.request) {
          // The request was made but no response was received
          console.log('No response received:', error.request);
          setReturnErrorMessage('Unableget_grid_supply_point to connect to Octopus Energy. Please try again.')
        } else {
          // Something happened in setting up the request that triggered an Error
          console.log('Request setup error:', error.message);
          setReturnErrorMessage('An unexpected error occurred. Please try again.')
        }
      }
    }
  };

  const handleChange = () => {
    onConnectionChange(false);
    setPropertyData(null);
    setExpandedProperties(new Set());
    setReturnErrorMessage('');
  };

  const validateInputs = () => {
    setReturnErrorMessage('');
    let isValid = true;

    if (!apiKeyValue) {
        setApiKeyError(true);
        setApiKeyErrorMessage('Please enter your API key.');
        isValid = false;
    } else {
      setApiKeyError(false);
      setApiKeyErrorMessage('');
    }
    if (!accountNumberValue) {
        setAccountNumberError(true);
        setAccountNumberErrorMessage('Please enter your Octopus Energy account number.');
        isValid = false;    
    } else {
      setAccountNumberError(false);
      setAccountNumberErrorMessage('');
    }

    return isValid;
  };

  // Helper function to format address
  const formatAddress = (property: any) => {
    const parts = [
      property.address_line_1,
      property.address_line_2,
      property.address_line_3,
      property.town,
      property.county,
      property.postcode
    ].filter(part => part && part.trim() !== '');
    
    return parts.join(', ');
  };

  // Helper function to handle property expansion
  const handleExpandProperty = (propertyIndex: number) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyIndex)) {
      newExpanded.delete(propertyIndex);
    } else {
      newExpanded.add(propertyIndex);
    }
    setExpandedProperties(newExpanded);
  };


  return (
    <Card variant="outlined" sx={{ backgroundColor: 'background.paper' }}>
      <Grid container alignItems="center" columns={16}>
        <Grid size={2}>
            <OctopusIcon />
        </Grid>
        <Grid size={14}>
            <Typography variant="h5">
            {isConnected ? 'Account successfully connected' : 'Link your Octopus Energy account'}
            </Typography>
        </Grid>
      </Grid>
      <Divider />
      
      {/* Property Summary Section - Show when connected */}
      {isConnected && propertyData && propertyData.properties && propertyData.properties.length > 0 && (
        <Box>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Properties Found
          </Typography>
          
          <Stack spacing={2}>
            {propertyData.properties.map((property: any, propertyIndex: number) => (
              <React.Fragment key={propertyIndex}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <HomeIcon sx={{ color: 'text.primary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1" color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                      {formatAddress(property)}
                    </Typography>
                    <Typography variant="body1" color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                      {property.region_name || 'N/A'}
                    </Typography>
                  </Box>
                  <IconButton 
                    onClick={() => handleExpandProperty(propertyIndex)}
                    size="small"
                  >
                  {expandedProperties.has(propertyIndex) ? 
                    <ExpandLessIcon /> : 
                    <ExpandMoreIcon />
                  }
                  </IconButton>
                </Stack>

                <Collapse in={expandedProperties.has(propertyIndex)}>
                  <Divider sx={{ my: 2 }} />

                  {/* Electricity Meters */}
                  {property.electricity_meters && property.electricity_meters.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <BoltRoundedIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="body1" color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                          Electricity Meters
                        </Typography>
                      </Stack>
                       
                      {property.electricity_meters.map((meter: any, meterIndex: number) => (
                        <Box key={meterIndex} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }} color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                            MPAN: {meter.mpan} | Serial: {meter.serial_number}
                          </Typography>                           
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Gas Meters */}
                  {property.gas_meters && property.gas_meters.length > 0 && (
                    <Box>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <SpeedRoundedIcon sx={{ color: 'text.primary' }} />
                        <Typography variant="body1" color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                          Gas Meters
                        </Typography>
                      </Stack>
                       
                      {property.gas_meters.map((meter: any, meterIndex: number) => (
                        <Box key={meterIndex} sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ mb: 1 }} color={theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary'}>
                            MPRN: {meter.mprn} | Serial: {meter.serial_number}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Collapse>
              </React.Fragment>
            ))}
          </Stack>
        </Box>
      )}

      <Box
        sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2 }}
      >
        <LoadingProgress 
          open={showProgress} 
          loadedTo={loadedToDate} 
          progress={agreementPercentComplete}
          showAgreementDetails={true}
          tariffName={tariffName}
          agreementPeriod={agreementPeriod}
        />
        <LoadingProgress 
          open={showConsumptionProgress} 
          loadedTo={loadedToDate} 
          progress={consumptionPercentComplete} 
        />
        <ConnectingProgress open={showConnecting} service='Octopus Energy' />

        {!isConnected && (
          <>
            <Typography variant='body2'>
              Please enter your Octopus Energy account details.
            </Typography>

            <Typography variant='body1'>
              This information is used to allow us to retreive your data from Octopus Energy. It is not shared with any third parties.
            </Typography>

            <FormControl sx={{ width: '100%'}}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormLabel htmlFor="apiKey">Your Octopus Energy API key</FormLabel>
              </Box>
              <TextField
                error={apiKeyError}
                helperText={apiKeyErrorMessage}
                name="apiKey"
                placeholder="sk_live_"
                type="apiKey"
                id="apiKey"
                autoComplete="current-apiKey"
                autoFocus={!isConnected}
                required
                fullWidth
                variant="outlined"
                color={apiKeyError ? 'error' : 'primary'}
                value={apiKeyValue}
                onChange={(e) => setApiKeyValue(e.target.value)}
                slotProps={{
                  input: {
                    readOnly: isConnected
                  }
                }}
              />
            </FormControl>        
            <FormControl sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <FormLabel htmlFor="accountNumber">Your Octopus Energy account number</FormLabel>
              </Box>
              <TextField
                error={accountNumberError}
                helperText={accountNumberErrorMessage}
                name="accountNumber"
                placeholder="A-00A000A0"
                type="accountNumber"
                id="accountNumber"
                autoComplete="current-accountNumber"
                autoFocus={!isConnected}
                required
                fullWidth
                variant="outlined"
                color={accountNumberError ? 'error' : 'primary'}
                value={accountNumberValue}
                onChange={(e) => setAccountNumberValue(e.target.value)}
                slotProps={{
                  input: {
                    readOnly: isConnected
                  }
                }}
              />
            </FormControl>
            <Typography variant='body1' color='error' align='center'>
              {returnErrorMessage}
            </Typography>
        
        </>
        )}

        <Button 
          onClick={isConnected ? handleChange : handleConnect}
          fullWidth 
          variant={isConnected ? "outlined" : "contained"}
          sx={isConnected ? {
            backgroundColor: theme.palette.background.paper,
            borderColor: theme.palette.divider,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.divider,
            }
          } : {}}
        >
          {isConnected ? 'Change' : 'Connect'}
        </Button>  
        {!isConnected && (
          <Typography sx={{ textAlign: 'center' }}>
            Don't have an API key?{' '}
            <span>
              <Link
                href="https://octopus.energy/dashboard/new/accounts/personal-details/api-access"
                target="_blank"
                rel="noopener"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                Generate one here
              </Link>
            </span>
          </Typography>
        )} 
        
      </Box>
    </Card>
  );
}
